import { useEffect, useRef } from 'react';
import { useTradingEventLog } from '@/contexts/TradingEventLogContext';
import {
  formatTradingEventTime,
  tradingEventCategoryLabel,
  tradingEventOrderFillMetrics,
  formatTradingEventFillNumber,
} from '@/lib/tradingEventDisplay';
import type { TradingEvent } from '@/services/tradingEventsApi';
import './TradingEventLog.css';

function EventLogRow({ event }: { event: TradingEvent }) {
  const fillMetrics = tradingEventOrderFillMetrics(event);

  return (
    <div className={`trading-event-log-row trading-event-log-row--${event.severity}`}>
      <span className="trading-event-log-row-time">{formatTradingEventTime(event.ts_ms)}</span>
      <span className="trading-event-log-row-category">{tradingEventCategoryLabel(event.category)}</span>
      <div className="trading-event-log-row-body">
        <span className="trading-event-log-row-title">{event.title}</span>
        <span className="trading-event-log-row-message">{event.message}</span>
        {fillMetrics ? (
          <span className="trading-event-log-row-fill">
            Token {formatTradingEventFillNumber(fillMetrics.tokenSize)} · USD{' '}
            {formatTradingEventFillNumber(fillMetrics.notionalUsd, 2)}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function TradingEventLog() {
  const { events, clearEvents } = useTradingEventLog();
  const listRef = useRef<HTMLDivElement>(null);
  const shouldStickToBottomRef = useRef(true);

  useEffect(() => {
    const list = listRef.current;
    if (!list || !shouldStickToBottomRef.current) return;
    list.scrollTop = list.scrollHeight;
  }, [events.length]);

  const onScroll = () => {
    const list = listRef.current;
    if (!list) return;
    const distanceFromBottom = list.scrollHeight - list.scrollTop - list.clientHeight;
    shouldStickToBottomRef.current = distanceFromBottom < 24;
  };

  return (
    <section className="trading-event-log" aria-label="事件日志">
      <header className="trading-event-log-head">
        <span className="trading-event-log-title">事件日志</span>
        <span className="trading-event-log-count">{events.length}</span>
        <button type="button" className="trading-event-log-clear" disabled={events.length === 0} onClick={clearEvents}>
          清空
        </button>
      </header>
      <div ref={listRef} className="trading-event-log-list" onScroll={onScroll}>
        {events.length === 0 ? (
          <p className="trading-event-log-empty">暂无事件，提交指令或订单后会在此显示。</p>
        ) : (
          events.map((event) => (
            <EventLogRow key={event.id} event={event} />
          ))
        )}
      </div>
    </section>
  );
}
